<template>
  <div v-if="visible" class="ai-voice-call-overlay">
    <div class="ai-voice-call-card" :class="'is-' + status">
      <div class="card-glow" aria-hidden="true" />
      <div class="avatar-wrap">
        <span v-if="status === 'connecting'" class="ring ring-1" />
        <span v-if="status === 'connecting'" class="ring ring-2" />
        <img :src="avatar" alt="avatar" class="avatar" @error="onAvatarError">
        <span v-if="status === 'connected'" class="pulse" />
      </div>
      <h3 class="name">{{ displayName }}</h3>
      <p class="status-text">
        <span class="status-dot" :class="status" />
        {{ statusText }}
      </p>
      <p v-if="status === 'connected'" class="timer">{{ formattedTime }}</p>

      <div class="controls">
        <button
          v-if="status === 'connected'"
          type="button"
          class="ctrl mute"
          :class="{ active: muted }"
          :title="muted ? $t('Unmute') : $t('Mute')"
          :aria-label="muted ? $t('Unmute') : $t('Mute')"
          @click="toggleMute"
        >
          <feather-icon :icon="muted ? 'MicOffIcon' : 'MicIcon'" size="24" />
        </button>
        <button
          type="button"
          class="ctrl hangup"
          :title="$t('End call')"
          :aria-label="$t('End call')"
          @click="hangup"
        >
          <feather-icon icon="PhoneOffIcon" size="26" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  requestAiVoiceCall,
  connectToAiRoom,
  setAiMicrophoneEnabled,
  disconnectAiRoom,
} from '@/services/AiVoiceCallService'

// eslint-disable-next-line global-require
const FALLBACK_AVATAR = require('@/assets/images/auth/female-avatar.png')

export default {
  name: 'AiVoiceCall',
  data() {
    return {
      visible: false,
      status: 'idle', // idle | connecting | connected | ended | error
      aiUser: null,
      callId: null,
      muted: false,
      seconds: 0,
      timer: null,
      avatarFallback: false,
      sessionId: 0,
      errorReason: null,
    }
  },
  computed: {
    displayName() {
      if (this.aiUser && this.aiUser.user_name) return this.aiUser.user_name
      return 'AI'
    },
    avatar() {
      if (this.avatarFallback) return FALLBACK_AVATAR
      const img = this.aiUser && this.aiUser.image
      if (img && (img.secure_url || img.url)) return img.secure_url || img.url
      if (typeof img === 'string' && img) return img
      return FALLBACK_AVATAR
    },
    statusText() {
      switch (this.status) {
        case 'connecting': return `${this.$t('Connecting')}…`
        case 'connected': return this.$t('Call in progress')
        case 'ended': return this.$t('Call ended')
        case 'error':
          if (this.errorReason === 'mic-not-found') return this.tWithFallback('Microphone not found', 'Mikrofon bulunamadı')
          if (this.errorReason === 'mic-denied') return this.tWithFallback('Microphone permission denied', 'Mikrofon izni verilmedi')
          return this.$t('Connection failed')
        default: return ''
      }
    },
    formattedTime() {
      const m = Math.floor(this.seconds / 60).toString().padStart(2, '0')
      const s = (this.seconds % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },
  },
  created() {
    // LiveKit Room nesnesi reaktif OLMAMALI (Vue içini sarmalarsa bağlantı bozulur)
    this.$_aiRoom = null
  },
  mounted() {
    this.$root.$on('ai-voice-call:start', this.startCall)
    this.$root.$on('ai-voice-call:end', this.hangup)
    window.addEventListener('pagehide', this.onPageHide)
    window.addEventListener('beforeunload', this.onPageHide)
  },
  beforeDestroy() {
    this.$root.$off('ai-voice-call:start', this.startCall)
    this.$root.$off('ai-voice-call:end', this.hangup)
    window.removeEventListener('pagehide', this.onPageHide)
    window.removeEventListener('beforeunload', this.onPageHide)
    this.cleanup()
  },
  methods: {
    async startCall(user) {
      if (this.status === 'connecting' || this.status === 'connected') return
      const aiUuid = user && (user.uuid || user.id)
      if (!aiUuid) {
        this.fail()
        return
      }
      this.visible = true
      this.status = 'connecting'
      this.errorReason = null
      this.muted = false
      this.seconds = 0
      this.avatarFallback = false
      // İlk gösterim için profildeki bilgiyi kullan, /start cevabı gelince güncellenir.
      this.aiUser = {
        user_name: user.user_name || user.firstName || user.name,
        image: (user.profile && user.profile.image) || user.image,
      }

      // Her arama için benzersiz oturum; kapatılan aramanın geç gelen cevapları yok sayılır.
      this.sessionId += 1
      const session = this.sessionId

      try {
        // Arayan kullanıcının uygulama dili (ör. 'tr'); backend bunu agent'a metadata ile iletir.
        const lang = (this.$i18n && this.$i18n.locale) || 'en'
        const body = await requestAiVoiceCall(aiUuid, lang)
        if (session !== this.sessionId) return // kullanıcı bu arada kapattı
        this.callId = body.call_id
        if (body.ai_user) this.aiUser = body.ai_user

        const room = await connectToAiRoom(body.server_url, body.participant_token, {
          onRoomCreated: r => {
            if (session === this.sessionId) this.$_aiRoom = r
          },
          onConnected: () => {
            if (session === this.sessionId) this.onConnected()
          },
          onDisconnected: () => {
            if (session === this.sessionId) this.onRoomDisconnected()
          },
          // AI (agent) görüşmeden ayrılırsa aramayı bitir
          onParticipantDisconnected: () => {
            if (session !== this.sessionId) return
            const r = this.$_aiRoom
            if (!r || !r.remoteParticipants || r.remoteParticipants.size === 0) this.endCall()
          },
        })
        if (session !== this.sessionId) {
          // Bağlanırken arama kapatıldı: odayı hemen kapat
          await disconnectAiRoom(room)
          return
        }
        this.$_aiRoom = room
      } catch (e) {
        if (session !== this.sessionId) return
        console.error('[AiVoiceCall] start error:', e)
        const name = e && e.name
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
          this.errorReason = 'mic-not-found'
        } else if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
          this.errorReason = 'mic-denied'
        }
        this.fail()
      }
    },
    onConnected() {
      if (this.status === 'ended') return
      this.status = 'connected'
      this.startTimer()
    },
    onRoomDisconnected() {
      if (this.status !== 'ended' && this.status !== 'idle') this.endCall()
    },
    startTimer() {
      this.clearTimer()
      this.timer = setInterval(() => { this.seconds += 1 }, 1000)
    },
    clearTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    },
    async toggleMute() {
      this.muted = !this.muted
      try {
        await setAiMicrophoneEnabled(this.$_aiRoom, !this.muted)
      } catch (e) {
        console.error('[AiVoiceCall] mute error:', e)
      }
    },
    async hangup() {
      await this.endCall()
    },
    async endCall() {
      if (this.status === 'ended') return
      this.status = 'ended'
      // Devam eden bağlanma işlemini geçersiz kıl
      this.sessionId += 1
      this.clearTimer()
      const room = this.$_aiRoom
      this.$_aiRoom = null
      await disconnectAiRoom(room)
      setTimeout(() => {
        if (this.status !== 'ended') return
        this.visible = false
        this.status = 'idle'
      }, 1200)
    },
    fail() {
      this.status = 'error'
      this.sessionId += 1
      this.clearTimer()
      const room = this.$_aiRoom
      this.$_aiRoom = null
      disconnectAiRoom(room)
      const delay = this.errorReason ? 3500 : 2000
      setTimeout(() => {
        this.visible = false
        this.status = 'idle'
      }, delay)
    },
    cleanup() {
      this.sessionId += 1
      this.clearTimer()
      const room = this.$_aiRoom
      this.$_aiRoom = null
      disconnectAiRoom(room)
    },
    onPageHide() {
      // Sekme/uygulama kapanırken aramayı sonlandır
      if (this.$_aiRoom || this.status === 'connecting' || this.status === 'connected') this.cleanup()
    },
    tWithFallback(key, trText) {
      // Çeviri dosyasında anahtar yoksa Türkçe arayüzde Türkçe metni göster
      const translated = this.$t(key)
      if (translated === key && this.$i18n && this.$i18n.locale === 'tr') return trText
      return translated
    },
    onAvatarError() {
      this.avatarFallback = true
    },
  },
}
</script>

<style scoped>
.ai-voice-call-overlay {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 20, 0.75);
  backdrop-filter: blur(6px);
}
.ai-voice-call-card {
  width: 320px;
  max-width: 90vw;
  padding: 32px 24px;
  border-radius: 20px;
  background: linear-gradient(160deg, #2b2150 0%, #1a1530 100%);
  color: #fff;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
}
.avatar-wrap {
  position: relative;
  width: 110px;
  height: 110px;
  margin: 0 auto 18px;
}
.avatar {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.18);
}
.pulse {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid #7c5cff;
  animation: ai-pulse 1.6s ease-out infinite;
}
@keyframes ai-pulse {
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(1.25); opacity: 0; }
}
.name {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}
.status-text {
  margin: 0;
  opacity: 0.7;
  font-size: 0.9rem;
}
.timer {
  margin: 8px 0 0;
  font-variant-numeric: tabular-nums;
  font-size: 1.1rem;
  letter-spacing: 1px;
}
.controls {
  margin-top: 26px;
  display: flex;
  gap: 12px;
  justify-content: center;
}
.ctrl {
  border: none;
  border-radius: 24px;
  padding: 10px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}
.ctrl.mute {
  background: rgba(255, 255, 255, 0.14);
}
.ctrl.mute.active {
  background: #b1573c;
}
.ctrl.hangup {
  background: #e03b4f;
}
.ctrl.hangup:hover {
  background: #c5283c;
}

/* ---------- Yeni koyu UI ---------- */
.ai-voice-call-overlay {
  background: rgba(6, 3, 20, 0.72);
}
.ai-voice-call-card {
  position: relative;
  overflow: hidden;
  width: 360px;
  padding: 36px 24px 30px;
  border-radius: 26px;
  background: linear-gradient(160deg, #2b1a63 0%, #1d1247 55%, #3a1356 100%);
  border: 1px solid rgba(234, 3, 178, 0.35);
  box-shadow: 0 24px 60px rgba(4, 1, 20, 0.7), 0 0 30px rgba(234, 3, 178, 0.2);
}
.card-glow {
  position: absolute;
  top: -30%;
  left: 50%;
  transform: translateX(-50%);
  width: 120%;
  height: 70%;
  background: radial-gradient(ellipse at center, rgba(234, 3, 178, 0.35), transparent 65%);
  pointer-events: none;
}
.avatar-wrap { z-index: 1; width: 120px; height: 120px; }
.avatar {
  position: relative;
  z-index: 2;
  width: 120px;
  height: 120px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px #ea03b2, 0 0 30px rgba(234, 3, 178, 0.55);
}
.pulse { border-color: #22c55e; }
.ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(234, 3, 178, 0.6);
  animation: ai-ring 2s ease-out infinite;
}
.ring-2 { animation-delay: 1s; }
@keyframes ai-ring {
  0% { transform: scale(1); opacity: 0.9; }
  100% { transform: scale(1.55); opacity: 0; }
}
.name { position: relative; z-index: 1; font-size: 22px; font-weight: 800; }
.status-text {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: 1;
  color: rgba(236, 232, 255, 0.82);
  font-size: 14px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 8px #f59e0b;
}
.status-dot.connected { background: #22c55e; box-shadow: 0 0 8px #22c55e; }
.status-dot.ended,
.status-dot.error { background: #ef4444; box-shadow: 0 0 8px #ef4444; }
.timer { position: relative; z-index: 1; color: #fff; font-weight: 700; }
.controls { position: relative; z-index: 1; margin-top: 28px; gap: 20px; }
.ctrl {
  width: 64px;
  height: 64px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform .15s ease, filter .15s ease;
}
.ctrl:hover { transform: scale(1.08); filter: brightness(1.1); }
.ctrl.mute {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
}
.ctrl.mute.active {
  background: rgba(239, 68, 68, 0.22);
  border-color: rgba(239, 68, 68, 0.55);
  color: #fca5a5;
}
.ctrl.hangup {
  background: linear-gradient(135deg, #ef4444, #e11d48);
  box-shadow: 0 8px 22px rgba(239, 68, 68, 0.5);
}
</style>
